import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { FetchError, queryKeys, ReorderPlanStructureDto, useAccessToken } from "src/http";
import { useSignalR } from "src/hooks";

type Variables = {
  planGuid: string;
  blocks: {
    guid: string;
    sortOrder: string;
    sections: {
      guid: string;
      sortOrder: string;
    }[];
  }[];
};

const useReorderPlanStructureRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function reorderPlanStructureRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: ReorderPlanStructureDto = {
      blocks: variables.blocks,
    };

    const response = await fetch(`${configuration.apiRootUrl}/plans/${variables.planGuid}/reorder-structure`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { reorderPlanStructureRequest };
};

const useReorderPlanStructure = () => {
  const { reorderPlanStructureRequest } = useReorderPlanStructureRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: reorderPlanStructureRequest,
    onSuccess: (_data, variables) => {
      signalR.sendRefetchPlan(variables.planGuid);
    },
    onSettled: (_data, _error, variables, _context) => {
      queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getStructure(variables.planGuid));
      queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
    },
  });
};

export { useReorderPlanStructure };
